import { useConvexAssessment } from '../services/assessments.convex'
import { AssessmentQuestionsTable } from './AssessmentQuestionsTable'
import type { Assessment } from '../types'
import {
  useCurrentOrganizationId,
  useMyConvexUserId,
  useOrganizationData,
} from '@/features/auth'
import { usePositionQuestions } from '@/features/positions'

interface ConnectedAssessmentQuestionsTableProps {
  assessmentId: string
}

export function ConnectedAssessmentQuestionsTable({
  assessmentId,
}: ConnectedAssessmentQuestionsTableProps) {
  const internalOrgId = useCurrentOrganizationId()
  const currentUserId = useMyConvexUserId()
  const userOrganizations = useOrganizationData()

  const raw = useConvexAssessment(assessmentId)

  // Adapt Convex shape → legacy field names expected by AssessmentQuestionsTable
  const assessment = raw
    ? {
        id: raw._id,
        employeeId: raw.employeeUserId,
        managerId: raw.managerUserId,
        organizationId: raw.organizationId,
        assessmentType: raw.assessmentType
          ? { ...raw.assessmentType, id: raw.assessmentType._id }
          : undefined,
        responses: raw.responses.map((r) => ({
          ...r,
          id: r._id,
          completedAt: r.completedAt
            ? new Date(r.completedAt).toISOString()
            : null,
        })),
      }
    : raw

  // Fetch position questions
  const {
    questions: positionQuestions,
    isLoading: isLoadingQuestions,
    error: questionsError,
  } = usePositionQuestions(raw?.assessmentType?.positionId)

  // Resolve organization name
  const currentOrg = userOrganizations.find((org) => org.id === internalOrgId)
  const organizationName = currentOrg?.name || 'your organization'
  const directReportName = raw?.employee?.name || 'Direct Report'

  return (
    <AssessmentQuestionsTable
      assessment={assessment as unknown as Assessment}
      positionQuestions={positionQuestions}
      currentUserId={currentUserId ?? ''}
      organizationName={organizationName}
      directReportName={directReportName}
      isLoading={raw === undefined || isLoadingQuestions}
      error={questionsError ?? null}
    />
  )
}
